/**
 * VersionDiffView Component
 * Shows differences between a file version and the current version
 */

import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react'
import { XMarkIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'

interface VersionDiffViewProps {
  isOpen: boolean
  onClose: () => void
  versionContent: string
  currentContent: string
  versionNumber: number
  fileName: string
  isDark: boolean
}

export const VersionDiffView: React.FC<VersionDiffViewProps> = ({
  isOpen,
  onClose,
  versionContent,
  currentContent,
  versionNumber,
  fileName,
  isDark
}) => {
  // LCS-based diff algorithm to properly match unchanged lines
  const diff = useMemo(() => {
    const oldLines = versionContent.split('\n')
    const newLines = currentContent.split('\n')
    
    // Normalize lines for comparison (trim trailing whitespace)
    const normalizeLine = (line: string) => line.trimEnd()
    
    // Build LCS table
    const buildLCSTable = (a: string[], b: string[]): number[][] => {
      const n = a.length
      const m = b.length
      const dp: number[][] = Array(n + 1)
        .fill(0)
        .map(() => Array(m + 1).fill(0))
      
      for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
          const aNorm = normalizeLine(a[i - 1])
          const bNorm = normalizeLine(b[j - 1])
          if (aNorm === bNorm) {
            dp[i][j] = dp[i - 1][j - 1] + 1
          } else {
            dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
          }
        }
      }
      return dp
    }
    
    // Backtrack to find LCS sequence
    const findLCS = (dp: number[][], a: string[], b: string[]): Set<number>[] => {
      const lcsSet: Set<number>[] = [new Set(), new Set()]
      let i = a.length
      let j = b.length
      
      while (i > 0 && j > 0) {
        const aNorm = normalizeLine(a[i - 1])
        const bNorm = normalizeLine(b[j - 1])
        if (aNorm === bNorm) {
          lcsSet[0].add(i - 1)
          lcsSet[1].add(j - 1)
          i--
          j--
        } else if (dp[i - 1][j] >= dp[i][j - 1]) {
          i--
        } else {
          j--
        }
      }
      return lcsSet
    }
    
    // Build diff from LCS
    const dp = buildLCSTable(oldLines, newLines)
    const [oldLCS, newLCS] = findLCS(dp, oldLines, newLines)
    
    const diffLines: Array<{
      type: 'unchanged' | 'deleted' | 'added' | 'modified'
      oldLine?: string
      newLine?: string
      oldLineNumber?: number
      newLineNumber?: number
    }> = []
    
    let oldIdx = 0
    let newIdx = 0
    
    while (oldIdx < oldLines.length || newIdx < newLines.length) {
      const oldInLCS = oldIdx < oldLines.length && oldLCS.has(oldIdx)
      const newInLCS = newIdx < newLines.length && newLCS.has(newIdx)
      
      if (oldInLCS && newInLCS) {
        // Both lines are in LCS - they match
        const oldLine = oldLines[oldIdx]
        const newLine = newLines[newIdx]
        const oldNorm = normalizeLine(oldLine)
        const newNorm = normalizeLine(newLine)
        
        if (oldNorm === newNorm) {
          // Truly unchanged
          diffLines.push({
            type: 'unchanged',
            oldLine,
            newLine,
            oldLineNumber: oldIdx + 1,
            newLineNumber: newIdx + 1
          })
        } else {
          // Modified (whitespace differences)
          diffLines.push({
            type: 'modified',
            oldLine,
            newLine,
            oldLineNumber: oldIdx + 1,
            newLineNumber: newIdx + 1
          })
        }
        oldIdx++
        newIdx++
      } else if (!oldInLCS && oldIdx < oldLines.length) {
        // Old line not in LCS - deleted
        diffLines.push({
          type: 'deleted',
          oldLine: oldLines[oldIdx],
          oldLineNumber: oldIdx + 1
        })
        oldIdx++
      } else if (!newInLCS && newIdx < newLines.length) {
        // New line not in LCS - added
        diffLines.push({
          type: 'added',
          newLine: newLines[newIdx],
          newLineNumber: newIdx + 1
        })
        newIdx++
      } else {
        // Shouldn't happen, but handle gracefully
        if (oldIdx < oldLines.length) oldIdx++
        if (newIdx < newLines.length) newIdx++
      }
    }
    
    return diffLines
  }, [versionContent, currentContent])

  const stats = useMemo(() => {
    const added = diff.filter(d => d.type === 'added').length
    const deleted = diff.filter(d => d.type === 'deleted').length
    const modified = diff.filter(d => d.type === 'modified').length
    return { added, deleted, modified, total: diff.length }
  }, [diff])

  // Find all change indices (added, deleted, or modified)
  const changeIndices = useMemo(() => {
    return diff
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.type === 'added' || item.type === 'deleted' || item.type === 'modified')
      .map(({ index }) => index)
  }, [diff])

  const [currentChangeIndex, setCurrentChangeIndex] = useState<number>(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToChange = useCallback((changeIndex: number) => {
    if (changeIndex < 0 || changeIndex >= changeIndices.length || !scrollContainerRef.current) return
    
    const diffIndex = changeIndices[changeIndex]
    const container = scrollContainerRef.current
    const diffContent = container.querySelector('.diff-content')
    if (!diffContent) return
    
    const children = diffContent.children
    if (!children || !children[diffIndex]) return
    
    const targetElement = children[diffIndex] as HTMLElement
    const containerRect = container.getBoundingClientRect()
    const targetRect = targetElement.getBoundingClientRect()
    const scrollTop = container.scrollTop
    const targetTop = targetRect.top - containerRect.top + scrollTop
    
    // Scroll to target with some offset from top for better visibility
    container.scrollTo({
      top: targetTop - 20, // 20px offset from top
      behavior: 'smooth'
    })
  }, [changeIndices])

  // Reset to first change when modal opens
  useEffect(() => {
    if (isOpen && changeIndices.length > 0) {
      setCurrentChangeIndex(0)
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToChange(0)
      }, 100)
    }
  }, [isOpen, changeIndices.length, scrollToChange])

  const handleNextChange = useCallback(() => {
    if (currentChangeIndex < changeIndices.length - 1) {
      const nextIndex = currentChangeIndex + 1
      setCurrentChangeIndex(nextIndex)
      scrollToChange(nextIndex)
    }
  }, [currentChangeIndex, changeIndices.length, scrollToChange])

  const handlePreviousChange = useCallback(() => {
    if (currentChangeIndex > 0) {
      const prevIndex = currentChangeIndex - 1
      setCurrentChangeIndex(prevIndex)
      scrollToChange(prevIndex)
    }
  }, [currentChangeIndex, scrollToChange])

  const canNavigateNext = currentChangeIndex < changeIndices.length - 1
  const canNavigatePrev = currentChangeIndex > 0

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={clsx(
              "w-full max-w-6xl max-h-[90vh] flex flex-col rounded-lg shadow-xl",
              isDark ? "bg-gray-800" : "bg-white"
            )}>
              {/* Header */}
              <div className={clsx(
                "flex items-center justify-between p-4 border-b",
                isDark ? "border-gray-700" : "border-gray-200"
              )}>
                <div>
                  <h2 className={clsx(
                    "text-lg font-semibold",
                    isDark ? "text-gray-100" : "text-gray-900"
                  )}>
                    Version {versionNumber} vs Current
                  </h2>
                  <p className={clsx(
                    "text-sm mt-1",
                    isDark ? "text-gray-400" : "text-gray-500"
                  )}>
                    {fileName}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Stats and Navigation */}
                  {changeIndices.length > 0 && (
                    <div className="flex items-center gap-2">
                      {/* Stats */}
                      <div className="flex items-center gap-2 text-xs">
                        {(stats.added > 0 || stats.modified > 0) && (
                          <span className={clsx(
                            "px-2 py-1 rounded",
                            isDark ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-800"
                          )}>
                            +{stats.added + stats.modified}
                          </span>
                        )}
                        {(stats.deleted > 0 || stats.modified > 0) && (
                          <span className={clsx(
                            "px-2 py-1 rounded",
                            isDark ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-800"
                          )}>
                            -{stats.deleted + stats.modified}
                          </span>
                        )}
                      </div>
                      {/* Navigation buttons */}
                      <div className="flex items-center gap-1 border-l pl-2 ml-1" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                        <button
                          onClick={handlePreviousChange}
                          disabled={!canNavigatePrev}
                          className={clsx(
                            "p-1 rounded transition-all",
                            canNavigatePrev
                              ? clsx(
                                  "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
                                  isDark ? "text-gray-300" : "text-gray-600"
                                )
                              : clsx(
                                  "cursor-not-allowed opacity-40",
                                  isDark ? "text-gray-600" : "text-gray-400"
                                )
                          )}
                          title="Previous change"
                        >
                          <ChevronUpIcon className="w-4 h-4" />
                        </button>
                        <span className={clsx(
                          "text-xs px-1",
                          isDark ? "text-gray-400" : "text-gray-500"
                        )}>
                          {changeIndices.length > 0 ? `${currentChangeIndex + 1}/${changeIndices.length}` : '0/0'}
                        </span>
                        <button
                          onClick={handleNextChange}
                          disabled={!canNavigateNext}
                          className={clsx(
                            "p-1 rounded transition-all",
                            canNavigateNext
                              ? clsx(
                                  "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
                                  isDark ? "text-gray-300" : "text-gray-600"
                                )
                              : clsx(
                                  "cursor-not-allowed opacity-40",
                                  isDark ? "text-gray-600" : "text-gray-400"
                                )
                          )}
                          title="Next change"
                        >
                          <ChevronDownIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={onClose}
                    className={clsx(
                      "p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700",
                      isDark ? "text-gray-400" : "text-gray-600"
                    )}
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Diff Content */}
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
                <div className="diff-content space-y-1 font-mono text-sm">
                  {diff.map((line, idx) => {
                    const isCurrentChange = changeIndices[currentChangeIndex] === idx
                    if (line.type === 'unchanged') {
                      return (
                        <div
                          key={idx}
                          className={clsx(
                            "px-3 py-1 flex items-start gap-4",
                            isDark ? "text-gray-400" : "text-gray-600"
                          )}
                        >
                          <span className="text-xs w-8 text-right opacity-50">
                            {line.oldLineNumber || line.newLineNumber || ''}
                          </span>
                          <span className="flex-1">{line.oldLine || line.newLine}</span>
                        </div>
                      )
                    } else if (line.type === 'deleted') {
                      return (
                        <div
                          key={idx}
                          className={clsx(
                            "px-3 py-1 flex items-start gap-4 transition-all",
                            isCurrentChange
                              ? clsx(
                                  "ring-2 ring-offset-2",
                                  isDark ? "bg-red-900/40 ring-red-500 ring-offset-gray-800" : "bg-red-100 ring-red-500 ring-offset-white"
                                )
                              : clsx(
                                  isDark ? "bg-red-900/20" : "bg-red-50"
                                )
                          )}
                        >
                          <span className="text-xs w-8 text-right opacity-50">
                            {line.oldLineNumber || ''}
                          </span>
                          <span className={clsx(
                            "flex-1 line-through",
                            isDark ? "text-red-300" : "text-red-800"
                          )}>
                            {line.oldLine}
                          </span>
                        </div>
                      )
                    } else if (line.type === 'added') {
                      return (
                        <div
                          key={idx}
                          className={clsx(
                            "px-3 py-1 flex items-start gap-4 transition-all",
                            isCurrentChange
                              ? clsx(
                                  "ring-2 ring-offset-2",
                                  isDark ? "bg-green-900/40 ring-green-500 ring-offset-gray-800" : "bg-green-100 ring-green-500 ring-offset-white"
                                )
                              : clsx(
                                  isDark ? "bg-green-900/20" : "bg-green-50"
                                )
                          )}
                        >
                          <span className="text-xs w-8 text-right opacity-50">
                            {line.newLineNumber || ''}
                          </span>
                          <span className={clsx(
                            "flex-1",
                            isDark ? "text-green-300" : "text-green-800"
                          )}>
                            + {line.newLine}
                          </span>
                        </div>
                      )
                    } else {
                      // Modified
                      return (
                        <div key={idx} className={clsx(
                          "space-y-1 transition-all",
                          isCurrentChange && clsx(
                            "ring-2 ring-offset-2 rounded",
                            isDark ? "ring-yellow-500 ring-offset-gray-800" : "ring-yellow-500 ring-offset-white"
                          )
                        )}>
                          <div
                            className={clsx(
                              "px-3 py-1 flex items-start gap-4",
                              isCurrentChange
                                ? clsx(
                                    isDark ? "bg-red-900/40" : "bg-red-100"
                                  )
                                : clsx(
                                    isDark ? "bg-red-900/20" : "bg-red-50"
                                  )
                            )}
                          >
                            <span className="text-xs w-8 text-right opacity-50">
                              {line.oldLineNumber || ''}
                            </span>
                            <span className={clsx(
                              "flex-1 line-through",
                              isDark ? "text-red-300" : "text-red-800"
                            )}>
                              - {line.oldLine}
                            </span>
                          </div>
                          <div
                            className={clsx(
                              "px-3 py-1 flex items-start gap-4",
                              isCurrentChange
                                ? clsx(
                                    isDark ? "bg-green-900/40" : "bg-green-100"
                                  )
                                : clsx(
                                    isDark ? "bg-green-900/20" : "bg-green-50"
                                  )
                            )}
                          >
                            <span className="text-xs w-8 text-right opacity-50">
                              {line.newLineNumber || ''}
                            </span>
                            <span className={clsx(
                              "flex-1",
                              isDark ? "text-green-300" : "text-green-800"
                            )}>
                              + {line.newLine}
                            </span>
                          </div>
                        </div>
                      )
                    }
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
